import { useEffect, useRef, useState } from "react";

import { Backdrop, Button, CircularProgress, Paper, Stack, Typography } from "@mui/material";

import {
  FIRST_WAIT_MS,
  KEEP_AWAKE_HOURS,
  POLL_GAP_MS,
  POLL_TIMEOUT_MS,
  READY_AUTO_CONTINUE_S,
  formatUntil,
  isDemoTenant,
  keepAwake,
  keepWarmStatus,
  probe,
  shouldSkip,
} from "../app/wake";
import { getApiUrl, getProductConfig } from "../config/product";

type Phase = "checking" | "waking" | "ready" | "hidden";

// House rules (CLAUDE.md): sentence-case buttons (the theme does not override MUI's ALL-CAPS), radius 4.
const btnSx = { textTransform: "none", borderRadius: 1 } as const;

/**
 * Full-screen notice while a scaled-to-zero backend boots, then a "Keep awake" choice (every tenant with
 * keep-warm enabled; wording differs for demo and prod).
 * Waits for sign-in (Aaron 2026-09-27: "move the new loader spin up message after the log in screen"): the
 * cold start the user notices is the skeleton after login, not the login form, which needs no backend.
 * Mounted in App; index.tsx prewarms /health silently at page load so the boot overlaps the sign-in.
 * Renders nothing unless the backend takes longer than FIRST_WAIT_MS to answer /health. The app keeps
 * loading underneath; this only explains the wait. Logic and rationale: src/app/wake.ts.
 */
const WakeGate: React.FC<{ signedIn: boolean }> = ({ signedIn }) => {
  const api = getApiUrl();
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const demo = isDemoTenant(host, getProductConfig().demo);
  const skip = shouldSkip(api, host, process.env.NODE_ENV);

  const [phase, setPhase] = useState<Phase>(skip ? "hidden" : "checking");
  const [elapsed, setElapsed] = useState(0);
  const [countdown, setCountdown] = useState(READY_AUTO_CONTINUE_S);
  const [until, setUntil] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const started = useRef(Date.now());

  // Once signed in, probe; only if it is slow, show the screen and keep polling until the backend answers.
  useEffect(() => {
    if (skip || !signedIn) return;
    started.current = Date.now();
    let cancelled = false;
    const slow = setTimeout(() => !cancelled && setPhase((p) => (p === "checking" ? "waking" : p)), FIRST_WAIT_MS);
    (async () => {
      let up = await probe(api, POLL_TIMEOUT_MS);
      while (!up && !cancelled) {
        await new Promise((r) => setTimeout(r, POLL_GAP_MS));
        up = await probe(api, POLL_TIMEOUT_MS);
      }
      if (cancelled) return;
      clearTimeout(slow);
      const wasSlow = Date.now() - started.current >= FIRST_WAIT_MS;
      if (!wasSlow) return setPhase("hidden");
      const st = await keepWarmStatus(api);
      if (cancelled) return;
      if (!st.enabled) return setPhase("hidden");
      if (st.message) setNote(st.message);
      if (st.until) setUntil(formatUntil(st.until));
      setPhase("ready");
    })();
    return () => {
      cancelled = true;
      clearTimeout(slow);
    };
  }, [api, demo, skip, signedIn]);

  // Elapsed counter while waking; auto-continue countdown once ready.
  useEffect(() => {
    if (phase !== "waking" && phase !== "ready") return;
    const t = setInterval(() => {
      if (phase === "waking") setElapsed(Math.round((Date.now() - started.current) / 1000));
      else setCountdown((c) => (c <= 1 ? 0 : c - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [phase]);

  useEffect(() => {
    if (phase === "ready" && countdown === 0 && !busy) setPhase("hidden");
  }, [phase, countdown, busy]);

  if (phase === "hidden" || phase === "checking") return null;

  const choose = async (hours: number) => {
    setBusy(true);
    const r = await keepAwake(api, hours);
    setBusy(false);
    if (r.message) return setNote(r.message);
    setUntil(formatUntil(r.until));
    setNote("");
    setTimeout(() => setPhase("hidden"), 2500);
  };

  return (
    // Backdrop defaults to aria-hidden, which would hide the whole dialog from screen readers.
    <Backdrop open aria-hidden={false} sx={{ zIndex: (t) => t.zIndex.modal + 10, p: 2 }}>
      <Paper role="dialog" aria-modal="true" aria-live="polite" aria-label="Environment status"
        sx={{ maxWidth: 480, width: "100%", p: 3, borderRadius: 1 }}>
        {phase === "waking" ? (
          <Stack spacing={2} alignItems="flex-start">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={22} />
              <Typography variant="h6">{demo ? "Waking up this demo environment" : "Starting up…"}</Typography>
            </Stack>
            {demo ? (
              <Typography variant="body2" color="text.secondary">
                Demo environments go to sleep when nobody is using them, so they cost almost nothing while idle.
                Waking one takes about 30–90 seconds, and after that it's fast. You can keep it awake for your
                session once it's up.
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Your Aeronet servers pause when nobody has used them for a while, which keeps running costs down.
                Starting them again takes about 30–90 seconds, and after that it's fast. The page will continue on
                its own, and you can keep it awake for your session once it's up.
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {elapsed}s
            </Typography>
            <Button size="small" sx={btnSx} onClick={() => setPhase("hidden")}>
              Continue anyway
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Typography variant="h6">Ready</Typography>
            <Typography variant="body2" color="text.secondary">
              {until
                ? `Kept awake until ${until}. Pick again to change it.`
                : demo
                  ? "Keep it awake so it doesn't go back to sleep while you work or demo:"
                  : "Keep it awake so it doesn't pause again while you work:"}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {KEEP_AWAKE_HOURS.map((h) => (
                <Button key={h} variant="outlined" sx={btnSx} disabled={busy} onClick={() => choose(h)}>
                  Keep awake {h}h
                </Button>
              ))}
            </Stack>
            {note && (
              <Typography variant="caption" color="warning.main">
                {note}
              </Typography>
            )}
            <Stack direction="row" justifyContent="flex-end">
              <Button variant="contained" sx={btnSx} onClick={() => setPhase("hidden")} disabled={busy}>
                Continue{busy ? "" : ` (${countdown})`}
              </Button>
            </Stack>
          </Stack>
        )}
      </Paper>
    </Backdrop>
  );
};

export default WakeGate;
